import { useMemo } from "react";
import { useAuth } from "@/features/core/auth/context/AuthContext";
import { useView } from "@/context/ViewContext";

/**
 * Two-Dimensional RBAC Enums
 */
export const Profile = Object.freeze({
  MANAGEMENT: "MANAGEMENT",
  VENDOR: "VENDOR",
  RESIDENT: "RESIDENT",
  PLATFORM: "PLATFORM",
});

export const AuthLevel = Object.freeze({
  OWNER: "OWNER",
  ADMIN: "ADMIN",
  COLLABORATOR: "COLLABORATOR",
  RESTRICTED: "RESTRICTED",
});

export const AUTH_LEVEL_RANKS = Object.freeze({
  [AuthLevel.OWNER]: 4,
  [AuthLevel.ADMIN]: 3,
  [AuthLevel.COLLABORATOR]: 2,
  [AuthLevel.RESTRICTED]: 1,
});

/**
 * Backend-aligned Canonical Permissions (22 Capabilities)
 */
export const Permission = Object.freeze({
  // 1. Organization & Members
  ORG_SETTINGS_WRITE: "org:settings:write",
  ORG_MEMBERS_MANAGE: "org:members:manage",
  ORG_BILLING_MANAGE: "org:billing:manage",

  // 2. Properties & Units
  PROPERTIES_READ: "properties:read",
  PROPERTIES_WRITE: "properties:write",
  PROPERTIES_DELETE: "properties:delete",
  PROPERTIES_FINANCIALS_READ: "properties:financials:read",
  PROPERTY_FINANCIALS_READ: "properties:financials:read", // Alias for backward compatibility

  // 3. Leasing & Applications
  APPLICATIONS_READ: "applications:read",
  APPLICATIONS_WRITE: "applications:write",
  APPLICATIONS_APPROVE: "applications:approve",
  APPLICATIONS_PII_READ: "applications:pii:read",

  // 4. Rentals & Contracts
  CONTRACTS_READ: "contracts:read",
  CONTRACTS_WRITE: "contracts:write",
  CONTRACTS_ACTIVATE: "contracts:activate",
  CONTRACTS_FINANCIALS_READ: "contracts:financials:read",

  // 5. Maintenance & Operations
  MAINTENANCE_READ: "maintenance:read",
  MAINTENANCE_WRITE: "maintenance:write",
  MAINTENANCE_UPDATE: "maintenance:write", // Canonical alias for update mutations
  MAINTENANCE_DISPATCH: "maintenance:dispatch",
  MAINTENANCE_APPROVE: "maintenance:approve",
  MAINTENANCE_BUDGET_READ: "maintenance:budget:read",
  MAINTENANCE_BUDGET_UPDATE: "maintenance:budget:read", // Canonical alias for budget updates
  MAINTENANCE_QUOTES_COMPARE: "maintenance:quotes:compare",

  // 6. General Financials & Ledger
  FINANCIAL_LEDGER_READ: "financial:ledger:read",
  FINANCIAL_PAYOUT_EXECUTE: "financial:payout:execute",
});

/**
 * Built-In Role Permission Fallbacks (Mirroring backend DEFAULT_ROLE_PERMISSIONS)
 */
export const DEFAULT_ROLE_PERMISSIONS = Object.freeze({
  OWNER: Object.values(Permission),
  SUPER_ADMIN: Object.values(Permission),
  PLATFORM_ADMIN: Object.values(Permission),
  ADMIN: [
    Permission.ORG_SETTINGS_WRITE,
    Permission.ORG_MEMBERS_MANAGE,
    Permission.PROPERTIES_READ,
    Permission.PROPERTIES_WRITE,
    Permission.PROPERTIES_DELETE,
    Permission.PROPERTIES_FINANCIALS_READ,
    Permission.APPLICATIONS_READ,
    Permission.APPLICATIONS_WRITE,
    Permission.APPLICATIONS_APPROVE,
    Permission.APPLICATIONS_PII_READ,
    Permission.CONTRACTS_READ,
    Permission.CONTRACTS_WRITE,
    Permission.CONTRACTS_ACTIVATE,
    Permission.CONTRACTS_FINANCIALS_READ,
    Permission.MAINTENANCE_READ,
    Permission.MAINTENANCE_WRITE,
    Permission.MAINTENANCE_DISPATCH,
    Permission.MAINTENANCE_APPROVE,
    Permission.MAINTENANCE_BUDGET_READ,
    Permission.MAINTENANCE_QUOTES_COMPARE,
  ],
  ORG_ADMIN: [
    Permission.ORG_SETTINGS_WRITE,
    Permission.ORG_MEMBERS_MANAGE,
    Permission.PROPERTIES_READ,
    Permission.PROPERTIES_WRITE,
    Permission.PROPERTIES_DELETE,
    Permission.PROPERTIES_FINANCIALS_READ,
    Permission.APPLICATIONS_READ,
    Permission.APPLICATIONS_WRITE,
    Permission.APPLICATIONS_APPROVE,
    Permission.APPLICATIONS_PII_READ,
    Permission.CONTRACTS_READ,
    Permission.CONTRACTS_WRITE,
    Permission.CONTRACTS_ACTIVATE,
    Permission.CONTRACTS_FINANCIALS_READ,
    Permission.MAINTENANCE_READ,
    Permission.MAINTENANCE_WRITE,
    Permission.MAINTENANCE_DISPATCH,
    Permission.MAINTENANCE_APPROVE,
    Permission.MAINTENANCE_BUDGET_READ,
    Permission.MAINTENANCE_QUOTES_COMPARE,
  ],
  COLLABORATOR: [
    Permission.PROPERTIES_READ,
    Permission.PROPERTIES_WRITE,
    Permission.APPLICATIONS_READ,
    Permission.APPLICATIONS_WRITE,
    Permission.APPLICATIONS_APPROVE,
    Permission.CONTRACTS_READ,
    Permission.CONTRACTS_WRITE,
    Permission.CONTRACTS_ACTIVATE,
    Permission.MAINTENANCE_READ,
    Permission.MAINTENANCE_WRITE,
    Permission.MAINTENANCE_DISPATCH,
    Permission.MAINTENANCE_APPROVE,
    Permission.MAINTENANCE_QUOTES_COMPARE,
  ],
  MANAGER: [
    Permission.PROPERTIES_READ,
    Permission.PROPERTIES_WRITE,
    Permission.APPLICATIONS_READ,
    Permission.APPLICATIONS_WRITE,
    Permission.APPLICATIONS_APPROVE,
    Permission.CONTRACTS_READ,
    Permission.CONTRACTS_WRITE,
    Permission.CONTRACTS_ACTIVATE,
    Permission.MAINTENANCE_READ,
    Permission.MAINTENANCE_WRITE,
    Permission.MAINTENANCE_DISPATCH,
    Permission.MAINTENANCE_APPROVE,
    Permission.MAINTENANCE_QUOTES_COMPARE,
  ],
  MAINTENANCE_COORDINATOR: [
    Permission.PROPERTIES_READ,
    Permission.MAINTENANCE_READ,
    Permission.MAINTENANCE_WRITE,
    Permission.MAINTENANCE_DISPATCH,
    Permission.MAINTENANCE_APPROVE,
    Permission.MAINTENANCE_QUOTES_COMPARE,
  ],
  LEASING_MANAGER: [
    Permission.PROPERTIES_READ,
    Permission.APPLICATIONS_READ,
    Permission.APPLICATIONS_WRITE,
    Permission.APPLICATIONS_APPROVE,
    Permission.CONTRACTS_READ,
    Permission.CONTRACTS_WRITE,
    Permission.CONTRACTS_ACTIVATE,
  ],
  RESTRICTED: [
    Permission.PROPERTIES_READ,
    Permission.MAINTENANCE_READ,
    Permission.MAINTENANCE_WRITE,
  ],
  FIELD_STAFF: [
    Permission.PROPERTIES_READ,
    Permission.MAINTENANCE_READ,
    Permission.MAINTENANCE_WRITE,
  ],
  FIELD_WORKER: [
    Permission.PROPERTIES_READ,
    Permission.MAINTENANCE_READ,
    Permission.MAINTENANCE_WRITE,
  ],
  HANDYMAN: [
    Permission.PROPERTIES_READ,
    Permission.MAINTENANCE_READ,
    Permission.MAINTENANCE_WRITE,
  ],
  VENDOR: [
    Permission.MAINTENANCE_READ,
    Permission.MAINTENANCE_WRITE,
  ],
  CONTRACTOR: [
    Permission.MAINTENANCE_READ,
    Permission.MAINTENANCE_WRITE,
  ],
  TENANT: [],
  RESIDENT: [],
});

/**
 * Normalizes input profile strings to canonical Profile enum values
 * @param {string} rawProfile
 * @returns {string|null}
 */
export function normalizeProfile(rawProfile) {
  if (!rawProfile || typeof rawProfile !== "string") return null;
  const p = rawProfile.trim().toUpperCase();
  if (p === "OPERATIONS" || p === "MANAGEMENT") return Profile.MANAGEMENT;
  if (p === "CONTRACTOR" || p === "VENDOR") return Profile.VENDOR;
  if (p === "RESIDENT" || p === "TENANT" || p === "USER") return Profile.RESIDENT;
  if (p === "PLATFORM") return Profile.PLATFORM;
  if (Object.values(Profile).includes(p)) return p;
  return null;
}

/**
 * Normalizes input auth level strings to canonical AuthLevel enum values
 * @param {string} rawLevel
 * @returns {string|null}
 */
export function normalizeAuthLevel(rawLevel) {
  if (!rawLevel || typeof rawLevel !== "string") return null;
  const l = rawLevel.trim().toUpperCase();
  if (Object.hasOwn(AUTH_LEVEL_RANKS, l)) return l;
  return null;
}

/**
 * Canonical hook to evaluate the Two-Dimensional RBAC model (Profile × Auth Level).
 * Derives state strictly from verified authentication and current view context.
 */
export function usePermissions() {
  const { user, isAuthenticated, loading } = useAuth();
  const viewCtx = useView();
  const currentView = viewCtx?.currentView || null;

  return useMemo(() => {
    // ── Fail-Closed Invariant ───────────────────────────────────────────
    if (loading || !isAuthenticated || !user) {
      return {
        loading: Boolean(loading),
        isAuthenticated: false,
        user: null,
        profile: null,
        level: AuthLevel.RESTRICTED,
        isPlatformAdmin: false,
        organizationRole: null,
        // Profile Helpers
        isManagement: false,
        isVendor: false,
        isResident: false,
        isPlatform: false,
        isOperations: false,
        isContractor: false,
        isTenant: false,
        // AuthLevel Helpers
        isOwner: false,
        isAdmin: false,
        isCollaborator: false,
        isRestricted: true,
        isOwnerOrAdmin: false,
        // Comparison & Evaluation Helpers
        isLevelAtLeast: () => false,
        hasAuthLevel: () => false,
        hasPermission: () => false,
        hasAnyPermission: () => false,
        hasAllPermissions: () => false,
        canAccess: () => false,
        // Capability Matrix
        canViewOrgFinancials: false,
        canViewPropertyPricing: false,
        canViewProjectBudgets: false,
        canCompareQuotes: false,
        canViewContractorFinances: false,
        canManageProperties: false,
        canApproveWorkOrders: false,
        canAcceptQuotes: false,
        canManageTeam: false,
        canManageBilling: false,
      };
    }

    // ── 1. Resolve Profile ─────────────────────────────────────────────
    const isPlatformAdmin = Boolean(user.isPlatformAdmin);
    const rawRole = String(user.role || "").toUpperCase();
    const isManagementRole =
      [
        "PLATFORM_ADMIN",
        "SUPER_ADMIN",
        "OWNER",
        "ORG_ADMIN",
        "ADMIN",
        "MANAGER",
        "MAINTENANCE_COORDINATOR",
        "LEASING_MANAGER",
        "COLLABORATOR",
        "RESTRICTED",
        "FIELD_STAFF",
        "FIELD_WORKER",
        "HANDYMAN",
        "VIEWER",
      ].includes(rawRole) || isPlatformAdmin;

    let resolvedProfile = normalizeProfile(user.profile);
    if (!resolvedProfile) {
      if (user.isVendor || rawRole === "VENDOR" || rawRole === "CONTRACTOR") {
        resolvedProfile = Profile.VENDOR;
      } else if (user.isTenant || rawRole === "TENANT" || rawRole === "RESIDENT" || rawRole === "USER") {
        resolvedProfile = Profile.RESIDENT;
      } else if (currentView) {
        const viewProfile = normalizeProfile(currentView);
        if (viewProfile === Profile.MANAGEMENT) {
          if (isManagementRole) {
            resolvedProfile = Profile.MANAGEMENT;
          }
        } else {
          resolvedProfile = viewProfile;
        }
      }

      if (!resolvedProfile && isManagementRole) {
        resolvedProfile = Profile.MANAGEMENT;
      }
    }
    // Fail-Closed Invariant: Unmapped or unknown roles default strictly to RESIDENT
    if (!resolvedProfile) {
      resolvedProfile = Profile.RESIDENT;
    }

    // ── 2. Resolve AuthLevel ───────────────────────────────────────────
    const rawOrgRole = user.organizationRole || user.role || null;
    let resolvedLevel = null;

    if (isPlatformAdmin) {
      resolvedLevel = AuthLevel.OWNER;
    } else {
      const isVendorRole = Boolean(user.isVendor || rawRole === "VENDOR");
      const isTenantRole = Boolean(user.isTenant || rawRole === "TENANT" || rawRole === "USER");

      if (isVendorRole) {
        if (resolvedProfile === Profile.VENDOR) {
          resolvedLevel =
            normalizeAuthLevel(user.authLevel || user.organizationRole) ||
            (user.isSubcontractor ? AuthLevel.COLLABORATOR : AuthLevel.OWNER);
        } else {
          resolvedLevel = AuthLevel.RESTRICTED;
        }
      } else if (isTenantRole) {
        if (resolvedProfile === Profile.RESIDENT) {
          resolvedLevel =
            normalizeAuthLevel(user.authLevel || user.organizationRole) ||
            AuthLevel.COLLABORATOR;
        } else {
          resolvedLevel = AuthLevel.RESTRICTED;
        }
      } else {
        resolvedLevel = normalizeAuthLevel(user.authLevel || user.organizationRole);
        if (!resolvedLevel) {
          if (["SUPER_ADMIN", "PLATFORM_ADMIN", "OWNER"].includes(rawRole)) {
            resolvedLevel = AuthLevel.OWNER;
          } else if (["ORG_ADMIN", "ADMIN"].includes(rawRole)) {
            resolvedLevel = AuthLevel.ADMIN;
          } else if (["MANAGER", "MAINTENANCE_COORDINATOR"].includes(rawRole)) {
            resolvedLevel = AuthLevel.COLLABORATOR;
          } else if (["RESTRICTED", "VIEWER", "FIELD_WORKER", "FIELD_STAFF", "HANDYMAN"].includes(rawRole)) {
            resolvedLevel = AuthLevel.RESTRICTED;
          } else {
            resolvedLevel = AuthLevel.RESTRICTED;
          }
        }
      }
    }

    const currentRank = AUTH_LEVEL_RANKS[resolvedLevel] || 1;

    // ── 3. Helper Functions ────────────────────────────────────────────
    const isLevelAtLeast = (minLevel) => {
      if (!minLevel) return true;
      const normalized = normalizeAuthLevel(minLevel);
      if (!normalized) return false;
      return currentRank >= (AUTH_LEVEL_RANKS[normalized] || 0);
    };

    const hasAuthLevel = (requiredLevel) => isLevelAtLeast(requiredLevel);

    const isManagement = resolvedProfile === Profile.MANAGEMENT;
    const isVendor = resolvedProfile === Profile.VENDOR;
    const isResident = resolvedProfile === Profile.RESIDENT;
    const isPlatform = resolvedProfile === Profile.PLATFORM || isPlatformAdmin;

    // Permissions resolution: User permissions or fallback role defaults
    const effectivePerms =
      Array.isArray(user.permissions) && user.permissions.length > 0
        ? user.permissions
        : (DEFAULT_ROLE_PERMISSIONS[rawRole] || DEFAULT_ROLE_PERMISSIONS[resolvedLevel] || []);
    const permsSet = new Set(effectivePerms);

    const hasPermission = (permission) => {
      if (!permission) return false;
      if (isPlatformAdmin) return true;
      if (permsSet.has(permission)) return true;

      // Built-in capability mappings
      if (
        permission === Permission.FINANCIAL_LEDGER_READ ||
        permission === "financial:ledger:read" ||
        permission === "canViewOrgFinancials"
      ) {
        return isManagement && resolvedLevel === AuthLevel.OWNER;
      }
      if (
        permission === Permission.PROPERTIES_FINANCIALS_READ ||
        permission === "properties:financials:read" ||
        permission === "canViewPropertyPricing"
      ) {
        return isManagement && isLevelAtLeast(AuthLevel.COLLABORATOR);
      }
      if (
        permission === Permission.MAINTENANCE_BUDGET_READ ||
        permission === Permission.MAINTENANCE_BUDGET_UPDATE ||
        permission === "maintenance:budget:read" ||
        permission === "maintenance:budget:update" ||
        permission === "canViewProjectBudgets"
      ) {
        return isManagement && isLevelAtLeast(AuthLevel.ADMIN);
      }
      if (
        permission === Permission.MAINTENANCE_QUOTES_COMPARE ||
        permission === "maintenance:quotes:compare" ||
        permission === "canCompareQuotes"
      ) {
        return isManagement && isLevelAtLeast(AuthLevel.COLLABORATOR);
      }
      if (
        permission === Permission.PROPERTIES_WRITE ||
        permission === "properties:write" ||
        permission === "canManageProperties"
      ) {
        return isManagement && isLevelAtLeast(AuthLevel.COLLABORATOR);
      }
      if (
        permission === Permission.PROPERTIES_READ ||
        permission === "properties:read"
      ) {
        return isManagement || isResident;
      }
      if (
        permission === Permission.MAINTENANCE_APPROVE ||
        permission === "maintenance:approve" ||
        permission === "canApproveWorkOrders" ||
        permission === "canAcceptQuotes"
      ) {
        return isManagement && isLevelAtLeast(AuthLevel.COLLABORATOR);
      }
      if (
        permission === Permission.MAINTENANCE_READ ||
        permission === "maintenance:read"
      ) {
        return isManagement || isVendor;
      }
      if (
        permission === Permission.MAINTENANCE_WRITE ||
        permission === Permission.MAINTENANCE_UPDATE ||
        permission === "maintenance:write" ||
        permission === "maintenance:update"
      ) {
        return isManagement || isVendor;
      }
      if (
        permission === Permission.MAINTENANCE_DISPATCH ||
        permission === "maintenance:dispatch"
      ) {
        return isManagement && isLevelAtLeast(AuthLevel.COLLABORATOR);
      }
      if (
        permission === Permission.ORG_MEMBERS_MANAGE ||
        permission === "org:members:manage" ||
        permission === "canManageTeam"
      ) {
        return isManagement && isLevelAtLeast(AuthLevel.ADMIN);
      }
      if (
        permission === Permission.ORG_BILLING_MANAGE ||
        permission === "org:billing:manage" ||
        permission === "canManageBilling"
      ) {
        return isManagement && resolvedLevel === AuthLevel.OWNER;
      }
      if (permission === "canViewContractorFinances") {
        return Boolean(isVendor && isLevelAtLeast(AuthLevel.COLLABORATOR) && !user.isSubcontractor);
      }

      return false;
    };

    const hasAnyPermission = (...perms) => {
      const flat = perms.flat().filter(Boolean);
      if (flat.length === 0) return false;
      return flat.some((p) => hasPermission(p));
    };

    const hasAllPermissions = (...perms) => {
      const flat = perms.flat().filter(Boolean);
      if (flat.length === 0) return false;
      return flat.every((p) => hasPermission(p));
    };

    // ── 4. Capability Matrix ───────────────────────────────────────────
    const canViewOrgFinancials = hasPermission(Permission.FINANCIAL_LEDGER_READ) || (isManagement && resolvedLevel === AuthLevel.OWNER);
    const canViewPropertyPricing = hasPermission(Permission.PROPERTIES_FINANCIALS_READ) || (isManagement && isLevelAtLeast(AuthLevel.COLLABORATOR));
    const canViewProjectBudgets = hasPermission(Permission.MAINTENANCE_BUDGET_READ) || (isManagement && isLevelAtLeast(AuthLevel.ADMIN));
    const canCompareQuotes = hasPermission(Permission.MAINTENANCE_QUOTES_COMPARE) || (isManagement && isLevelAtLeast(AuthLevel.COLLABORATOR));
    const canViewContractorFinances = Boolean(isVendor && isLevelAtLeast(AuthLevel.COLLABORATOR) && !user.isSubcontractor);
    const canManageProperties = hasPermission(Permission.PROPERTIES_WRITE) || (isManagement && isLevelAtLeast(AuthLevel.COLLABORATOR));
    const canApproveWorkOrders = hasPermission(Permission.MAINTENANCE_APPROVE) || (isManagement && isLevelAtLeast(AuthLevel.COLLABORATOR));
    const canAcceptQuotes = hasPermission(Permission.MAINTENANCE_APPROVE) || (isManagement && isLevelAtLeast(AuthLevel.COLLABORATOR));
    const canManageTeam = hasPermission(Permission.ORG_MEMBERS_MANAGE) || (isManagement && isLevelAtLeast(AuthLevel.ADMIN));
    const canManageBilling = hasPermission(Permission.ORG_BILLING_MANAGE) || (isManagement && resolvedLevel === AuthLevel.OWNER);

    const capabilityMap = {
      canViewOrgFinancials,
      canViewPropertyPricing,
      canViewProjectBudgets,
      canCompareQuotes,
      canViewContractorFinances,
      canManageProperties,
      canApproveWorkOrders,
      canAcceptQuotes,
      canManageTeam,
      canManageBilling,
    };

    // ── 5. Composite Evaluation Helper ─────────────────────────────────
    const canAccess = ({
      profile: targetProfile,
      level: targetLevel,
      minLevel: targetMinLevel,
      authLevel: targetAuthLevel,
      permission: targetPerm,
      anyPermissions: targetAnyPerms,
      allPermissions: targetAllPerms,
    } = {}) => {
      if (targetProfile) {
        const allowed = Array.isArray(targetProfile) ? targetProfile : [targetProfile];
        const normalized = allowed.map(normalizeProfile).filter(Boolean);
        if (!normalized.includes(resolvedProfile)) return false;
      }
      const effectiveExactLevel = targetLevel;
      if (effectiveExactLevel) {
        const allowed = Array.isArray(effectiveExactLevel) ? effectiveExactLevel : [effectiveExactLevel];
        const normalized = allowed.map(normalizeAuthLevel).filter(Boolean);
        if (!normalized.includes(resolvedLevel)) return false;
      }
      const threshold = targetMinLevel || targetAuthLevel;
      if (threshold) {
        if (!isLevelAtLeast(threshold)) return false;
      }
      if (targetAnyPerms && targetAnyPerms.length > 0) {
        if (!hasAnyPermission(targetAnyPerms)) return false;
      }
      if (targetAllPerms && targetAllPerms.length > 0) {
        if (!hasAllPermissions(targetAllPerms)) return false;
      }
      if (Array.isArray(targetPerm) && targetPerm.length === 0) {
        return false;
      }
      if (targetPerm) {
        const allowed = Array.isArray(targetPerm) ? targetPerm : [targetPerm];
        if (allowed.length === 0) return false;
        const satisfies = allowed.every((p) => {
          if (typeof p === "string" && Object.hasOwn(capabilityMap, p)) {
            return Boolean(capabilityMap[p]);
          }
          return hasPermission(p);
        });
        if (!satisfies) return false;
      }
      return true;
    };

    return {
      loading: false,
      isAuthenticated: true,
      user,
      profile: resolvedProfile,
      level: resolvedLevel,
      isPlatformAdmin,
      organizationRole: rawOrgRole,
      // Profile Helpers
      isManagement,
      isVendor,
      isResident,
      isPlatform,
      isOperations: isManagement,
      isContractor: isVendor,
      isTenant: isResident,
      // AuthLevel Helpers
      isOwner: resolvedLevel === AuthLevel.OWNER,
      isAdmin: resolvedLevel === AuthLevel.ADMIN,
      isCollaborator: resolvedLevel === AuthLevel.COLLABORATOR,
      isRestricted: resolvedLevel === AuthLevel.RESTRICTED,
      isOwnerOrAdmin: resolvedLevel === AuthLevel.OWNER || resolvedLevel === AuthLevel.ADMIN,
      // Comparison & Evaluation Helpers
      isLevelAtLeast,
      hasAuthLevel,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      canAccess,
      // Capability Matrix
      canViewOrgFinancials,
      canViewPropertyPricing,
      canViewProjectBudgets,
      canCompareQuotes,
      canViewContractorFinances,
      canManageProperties,
      canApproveWorkOrders,
      canAcceptQuotes,
      canManageTeam,
      canManageBilling,
    };
  }, [user, isAuthenticated, loading, currentView]);
}

export default usePermissions;
